<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12" class="pr-6">
      <sidebar-row>
        <v-row no-gutters align="center" style="flex-wrap: nowrap">
          <v-col cols="auto" class="pa-0 flex-shrink-1 overflow-hidden mr-3">
            <v-row no-gutters align="center">
              <v-col cols="auto" class="pa-0 text-body-2 font-weight-bold text-truncate">
                <span>{{ title }}</span>
              </v-col>
            </v-row>
          </v-col>
          <template v-if="enableZoomHighlight">
            <v-col cols="auto" v-if="!disableZoomAll">
              <v-btn icon @click="zoomToAll">
                <v-icon size="20">
                  mdi-magnify
                </v-icon>
              </v-btn>
            </v-col>
            <v-col cols="auto" class="py-2">
              <v-row no-gutters>
                <v-col cols="auto">
                  <v-switch v-model="highlightAll" hide-details :ripple="false">
                    <template #label>
                      <span class="text-body-2">
                        {{ $i18n.t('admin.joins.highlight') }}
                      </span>
                    </template>
                  </v-switch>
                </v-col>
              </v-row>
            </v-col>
          </template>
        </v-row>
      </sidebar-row>
      <sidebar-row has-icon-column border-bottom v-if="collisionsLoading">
        <main-loader class="pb-5" />
      </sidebar-row>
      <sidebar-row has-icon-column border-bottom v-else paddingRight="">
        <slot name="prepend-tabs" />
        <v-tabs v-model="currentTab" hide-slider class="custom-tabs" :show-arrows="false" v-if="hasTabs">
          <v-col cols="auto" class="px-0 pr-4" v-for="tab in computedTabs" :key="tab.value">
            <v-badge
              overlap
              :content="computedCollisionsTreesCounts[tab.scope]"
              :value="computedCollisionsTreesCounts[tab.scope] && computedCollisionsTreesCounts[tab.scope].length"
              color="error"
            >
              <v-tab v-text="tab.text" active-class="activeTab"> </v-tab>
            </v-badge>
          </v-col>
        </v-tabs>
        <v-row no-gutters class="pb-7">
          <v-col class="pa-0">
            <v-tabs-items v-model="currentTab">
              <v-tab-item v-for="tab in computedTabs" :key="tab.value">
                <v-row no-gutters>
                  <v-col cols="12" class="text-left">
                    <v-treeview :items="computedCollisionsTrees[tab.scope]">
                      <template #label="data">
                        <v-row no-gutters>
                          <v-col cols="10">
                            <v-row no-gutters v-on="getListeners(data)">
                              <v-col cols="auto" class="text-body-2">
                                {{ data.item.name }}
                              </v-col>
                            </v-row>
                            <v-row no-gutters v-if="data.item.subName">
                              <v-col
                                cols="auto"
                                class="font-italic font-weight-light italic--text"
                                style="font-size: 12px"
                              >
                                {{ data.item.subName }}
                              </v-col>
                            </v-row>
                          </v-col>
                          <v-spacer />
                          <v-col cols="1" class="text-right" v-if="data.item.geometry">
                            <v-icon size="18" @click="zoomToFeature(data.item)"> mdi-magnify </v-icon>
                          </v-col>
                        </v-row>
                      </template>
                    </v-treeview>
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
        <v-row no-gutters class="pr-5 pb-5" align="center" v-if="!hideBuffer">
          <v-col class="pa-0 mr-7" cols="5">
            <data-input v-model="bufferValue" v-bind="bufferInput" />
          </v-col>
          <v-col class="pa-0" cols="auto">
            <base-button
              class="text-body-2"
              translation-path="sidebar.watSewCollisionsInfo.check"
              @click="getCollisions(layerId, featureId, bufferValue)"
              :loading="collisionsBufferLoading"
              :disabled="!bufferValue"
            />
          </v-col>
        </v-row>
      </sidebar-row>
    </v-col>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import BaseButton from '@/components/BaseButton';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';

import getBbox from '@/mixins/map/getBbox';

export default {
  name: 'WatSewCollisionsInfo',
  components: { BaseButton, DataInput, MainLoader, SidebarRow },
  mixins: [getBbox],
  props: {
    customCollisionsTrees: {
      type: Object,
    },
    customCollisionsTreesCounts: {
      type: Object,
    },
    hideBuffer: {
      type: Boolean,
      default: false,
    },
    hoverColor: {
      type: String,
      default: '35,119,234',
    },
    enableZoomHighlight: {
      type: Boolean,
      default: false,
    },
    disableZoomAll: {
      type: Boolean,
      default: false,
    },
    layerId: {
      type: [Number, String],
      default: null,
    },
    featureId: {
      type: [Number, String],
      default: null,
    },
    tabs: {
      type: Array,
      default: () => {
        return [
          {
            value: 'water',
            moduleName: 'WATER_DATA',
            settingName: 'water_data_module_enabled',
            scope: 'water',
          },
          {
            value: 'sewer',
            moduleName: 'SEWER_DATA',
            settingName: 'sewer_data_module_enabled',
            scope: 'sewer',
          },
        ];
      },
    },
    titlePath: {
      type: String,
      default: 'sidebar.watSewCollisionsInfo.watSewCollisionsTitle',
    },
    titleStraight: {
      type: String,
    },
  },
  computed: {
    additionalModules: get('admin/additionalModules'),
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    permissions: get('users/toolsPermissions'),
    settings: get('admin/settingsValues'),

    computedCollisionsTrees() {
      return this.customCollisionsTrees || this.collisionsTrees;
    },
    computedCollisionsTreesCounts() {
      return this.customCollisionsTreesCounts || this.collisionsTreesCounts;
    },

    bufferInput() {
      return {
        dataType: { name: 'float' },
        hideDetails: true,
        label: this.$i18n.t('sidebar.watSewCollisionsInfo.bufferLabel'),
        disabled: this.collisionsBufferLoading,
        loading: this.collisionsBufferLoading,
        allowNegative: false,
      };
    },
    computedTabs() {
      return this.tabs
        .filter(tab => this.isTabEnabled(tab))
        .map(tab => {
          const { value, scope } = tab;
          return {
            value,
            ...(this.hasTabs ? { text: this.$i18n.t(`sidebar.watSewCollisionsInfo.tabsNames.${value}`) } : {}),
            scope,
          };
        });
    },
    tabsScopes() {
      return this.tabs.map(tab => tab.scope);
    },

    hasTabs() {
      return this.tabs?.length > 1;
    },
    title() {
      return this.titleStraight || this.$i18n.t(this.titlePath);
    },
    computedHighlightingTrigger() {
      return {
        highlight: this.highlightAll,
        features: this.computedCollisionsTrees[this.tabs[this.currentTab]?.value]?.reduce(
          (acc, item) => [...acc, ...item.children.filter(child => child.geometry)],
          []
        ),
      };
    },
  },
  data: () => ({
    bufferValue: null,
    currentTab: 0,
    highlightAll: true,
    collisionsLoading: false,
    collisionsBufferLoading: false,
    collisionsTrees: {},
    collisionsTreesCounts: {},
  }),
  watch: {
    highlightAll(nV) {
      if (!this.enableZoomHighlight) return;
      let currentStorage = JSON.parse(localStorage.getItem('joins-highlight-disabled-layers') || '[]');
      currentStorage = currentStorage.filter(layer => layer !== this.layerId);
      currentStorage = nV ? currentStorage : [...currentStorage, this.layerId];
      localStorage.setItem('joins-highlight-disabled-layers', JSON.stringify(currentStorage));
    },
    computedHighlightingTrigger: {
      handler(nV) {
        if (!this.enableZoomHighlight || !nV.features?.length) return;
        this.$root.$emit(
          'toggleFeatureHover',
          this.computedCollisionsTrees[this.tabs[this.currentTab]?.value]?.reduce(
            (acc, item) => [...acc, ...item.children],
            []
          ),
          nV.highlight,
          'collisionsTempLayer'
        );
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    getWatSewCollisions: call('layers/getWatSewCollisions'),
    async getCollisions(layerId = this.layerId, featureId = this.featureId, bufferValue = this.bufferValue) {
      if ((!layerId && layerId !== 0) || (!featureId && featureId !== 0)) {
        return;
      }
      this[bufferValue ? 'collisionsBufferLoading' : 'collisionsLoading'] = true;
      try {
        const data = { layerId, featureId, bufferValue };
        const collisions = await this.getWatSewCollisions(data);
        if (bufferValue) {
          let features = [];
          Object.values(collisions).forEach((collisionsFeatures, cIdx) => {
            features = features.concat(
              collisionsFeatures.map((feature, fIdx) => {
                return {
                  type: 'Feature',
                  id: `${cIdx}-${fIdx}`,
                  properties: {},
                  geometry: feature.geometry,
                };
              })
            );
          });
          const geojson = {
            type: 'FeatureCollection',
            crs: {
              properties: {
                name: this.$_config.defaultEpsg,
              },
              type: 'name',
            },
            features,
          };
          this.$root.$emit('toggleWatSewCollisionBuffer', geojson);
        } else {
          const { collisionsTrees, collisionsTreesCounts } = this.getCollisionsTreeAndCounts(collisions);
          this.collisionsTrees = collisionsTrees;
          this.collisionsTreesCounts = collisionsTreesCounts;
        }
        this[bufferValue ? 'collisionsBufferLoading' : 'collisionsLoading'] = false;
      } catch {
        this[bufferValue ? 'collisionsBufferLoading' : 'collisionsLoading'] = false;
      }
    },
    async zoomToAll() {
      if (this.disableZoomAll) return;
      /**
       * Zooming is based on layers and it works only when `id` is layer's id and each child has `featureId` property.
       * Don't use it with datasource features or use openlayer's methods to create bbox.
       */
      const currentTabMetadata = this.computedCollisionsTrees[this.tabs[this.currentTab]?.value]
        ?.filter(layer => !!this.layers[layer.id])
        .map(layer => ({
          layerId: layer.id,
          featuresIds: layer.children.map(child => child.featureId),
        }));
      if (!currentTabMetadata.length) return;
      const bbox = await this.getMultipleLayersFeaturesBbox(currentTabMetadata);
      this.$root.$emit('fitView', bbox);
    },
    getCollisionsTreeAndCounts(data = {}) {
      const collisionsTrees = {};
      const collisionsTreesCounts = {};
      this.tabsScopes.forEach(tab => {
        collisionsTrees[tab] = [];
        collisionsTreesCounts[tab] = 0;
      });
      Object.keys(data).forEach(dataSourceName => {
        const dataSource = this.dataSources[dataSourceName];
        const { scope } = dataSource;
        if (this.tabsScopes.includes(scope)) {
          const dataSourceTreeElement = {
            id: dataSourceName,
            name: dataSource.verbose_name,
            children: data[dataSourceName].map((feature, idx) => {
              const { name, attribute_name, area, length, geometry } = feature;
              let subName = '';
              if (area || area === 0) {
                subName = this.$i18n.t('sidebar.watSewCollisionsInfo.area', { value: area.toFixed(3) });
              } else if (length || length === 0) {
                subName = this.$i18n.t('sidebar.watSewCollisionsInfo.length', { value: length.toFixed(3) });
              }
              const attributeName = dataSource.attributes_schema.attributes.find(
                attribute => attribute.name === attribute_name
              )?.verbose_name;
              return {
                id: `${name}-${idx}`,
                name: `${attributeName}: ${name}`,
                subName,
                geometry,
              };
            }),
          };
          collisionsTrees[scope].push(dataSourceTreeElement);
          collisionsTreesCounts[scope] += dataSourceTreeElement?.children.length || 0;
        }
      });
      return { collisionsTrees, collisionsTreesCounts };
    },
    isTabEnabled(tab) {
      const { moduleName, settingName } = tab;
      const {
        [moduleName]: { enabled: superAdminEnabled, configured },
      } = this.additionalModules;
      const { [settingName]: adminEnabled } = this.settings;
      const { [moduleName]: { main_value: modulePermissions } = {} } = this.permissions;
      return superAdminEnabled && adminEnabled && configured && modulePermissions > 0;
    },
    getListeners(data) {
      const { leaf = false } = data;
      if (!leaf || this.highlightAll) return {};
      return {
        mouseleave: () => {
          this.onHover(data, false);
        },
        mouseenter: () => {
          this.onHover(data, true);
        },
      };
    },
    onHover(data, value) {
      this.$root.$emit('toggleWatSewCollisionHover', data.item, { value, rgbColor: this.hoverColor });
    },
    zoomToFeature(item) {
      const geometry = JSON.parse(JSON.stringify(item.geometry));
      geometry.crs = { properties: { name: this.$_config.defaultEpsg }, type: 'name' };
      this.$root.$emit('createGeomAndFitView', geometry);
    },
  },
  mounted() {
    this.getCollisions();
    let currentStorage = JSON.parse(localStorage.getItem('joins-highlight-disabled-layers') || '[]');
    this.highlightAll = !currentStorage.includes(this.layerId);
  },
  beforeDestroy() {
    this.$root.$emit('toggleWatSewCollisionBuffer');
    this.$root.$emit('toggleFeatureHover', null, false, 'collisionsTempLayer');
  },
};
</script>
<style lang="scss" scoped>
.v-tab {
  letter-spacing: 0.0178571429em !important;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  height: fit-content;
  text-transform: unset !important;
}
.activeTab {
  background-color: map-get($colors, 'primarybackground');
}
.custom-tabs {
  position: sticky;
  z-index: 1;
}
.v-tab:not(.activeTab) {
  color: #5f6368 !important;
}
.joins-switch {
  ::v-deep .v-input__slot {
    padding-right: 0 !important;
  }
}

::v-deep {
  .v-input--switch {
    margin: 0 !important;
    padding: 0 !important;
  }
  .v-slide-group__prev {
    display: none !important;
  }
  .v-slide-group__next {
    display: none !important;
  }
}
</style>
